<template>
  <NuxtLayout
    :name="route.meta.embedded ? 'embedded-service' : 'self-service'"
    :page="page"
  >
    <section class="w-96 flex flex-col gap-y-4 m-auto max-mobile:w-full">
      <FormKit
        :actions="false"
        type="form"
        form-class="w-96 max-mobile:w-full border border-solid border-cool-gray-200 rounded"
        @submit="login"
        #default="{ disabled, state: { valid } }"
        :aria-invalid="hasLoginError"
        aria-errormessage="errorBanner"
      >
        <UtilsHeadline
          class="pt-11 pb-9 text-center"
          :level="1"
          :text="$t('dbc-head-profile')"
          :has-pulse="true"
        />

        <DbcBanner
          v-if="hasLoginError"
          id="errorBanner"
          :severity="'warn'"
          :message="$t('dbc-text-login-error')"
        />

        <DbcBanner
          v-if="banner"
          :id="banner.id ? banner.id : null"
          :severity="banner.severity"
          :message="banner.message"
        />

        <FormKit
          type="email"
          id="email"
          name="email"
          :label="$t('dbc-label-email')"
          :placeholder="$t('dbc-placeholder-email')"
          validation="required|email"
          prefix-icon="emailIcon"
          :floating-label="true"
          autocomplete="email"
        />

        <FormKit
          type="password"
          name="password"
          id="password"
          :label="$t('dbc-label-password')"
          :placeholder="$t('dbc-placeholder-password')"
          validation="required"
          prefix-icon="passwordIcon"
          suffix-icon="eyeClosedIcon"
          @suffix-icon-click="(node: FormKitNode ) => togglePasswordIcon(node)"
          :floating-label="true"
          autocomplete="current-password"
        />

        <FormKit
          type="submit"
          id="submit"
          :label="
            !valid
              ? $t('dbc-self-service-login-button-disabled-label')
              : $t('dbc-self-service-login-button-label')
          "
          :disabled="disabled || !valid || isLoading"
          :loading="isLoading"
          :aria-label="
            !valid
              ? $t('dbc-self-service-login-button-disabled-label')
              : !isLoading
              ? $t('dbc-self-service-login-button-label')
              : $t('dbc-text-loading')
          "
        />

        <NuxtLink
          v-if="!route.meta.embedded"
          class="text-placeholder text-center no-underline hover:underline"
          :to="getPagePath['password-reset-start'][locale]"
        >
          {{ $t('dbc-text-forgot-password') }}
        </NuxtLink>
      </FormKit>
      <div
        v-if="!route.meta.embedded"
        class="group bg-white p-4 border border-solid border-cool-gray-200 rounded flex justify-between"
      >
        <p class="w-[14ch]">
          {{ $t('dbc-text-not-yet-registered') }}
        </p>
        <NuxtLink
          class="text-placeholder text-center no-underline dbc-gc-btn dbc-gc-btn--ghost min-w-40"
          :to="getPagePath['register'][locale]"
        >
          {{ $t('dbc-text-register') }}
        </NuxtLink>
      </div>
    </section>
  </NuxtLayout>
</template>
<script setup lang="ts">
import type { FormKitNode } from '@formkit/core'
import togglePasswordIcon from '@/formkit/features/togglePasswordIcon'
import { useCustomerStore } from '../../stores/customer'

const route = useRoute()
const router = useRouter()

const { setLocale, t } = useI18n()
const locale = route.params.locale as string
setLocale(locale)

const mobileOs = useMobileOs()

const isLoading = ref(false)

const hasLoginError = ref(false)

const banner = ref<{
  id?: string
  severity: 'success' | 'warn' | 'info' | 'attention'
  message: string
} | null>(null)

if (route.query['pw-reset-start'] === '✓') {
  banner.value = {
    severity: 'success',
    message: t('dbc-text-success-reset-password-start').replace(
      '[placeholder]',
      useCustomerStore().resetPwEmail as string
    ),
  }
}

if (route.query['pw-reset-finish'] === '✓') {
  banner.value = {
    severity: 'success',
    message: t('dbc-text-success-reset-password-finish'),
  }
}

async function login(formData: { email: string; password: string }) {
  isLoading.value = true

  try {
    await fetchLogin(formData)
    hasLoginError.value = false
    if (route.query.redirect) {
      router.push(route.query.redirect as string)
    } else if (mobileOs.value === '') {
      router.push(usePagePath('data')!)
    } else {
      router.push(usePagePath('self-service')!)
    }
  } catch (e) {
    hasLoginError.value = true
  } finally {
    isLoading.value = false
  }
}

const page: Page = {
  id: 0,
  title: t('dbc-head-profile'),
  path: '/anmelden',
  description: '',
  content: [],
  localizations: [
    {
      id: 0,
      title: t('dbc-head-profile'),
      path: 'anmelden',
      locale: 'de',
    },
    {
      id: 1,
      title: t('dbc-head-profile'),
      path: 'login',
      locale: 'en',
    },
  ],
  locale: locale,
}
usePageHead(page)
</script>
